'use client';

import dynamic from 'next/dynamic';
import { type HTMLAttributes } from 'react';
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import { useTranslation } from '@/app/components/translations/Translation';
import styled, { css } from 'styled-components';
import {
  EvoModalSectionContainer,
  EvoSectionContainer,
  EvoSectionHeading,
} from '@/app/components/hotel-detail/evo/EvoCommon';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import customCss from './css/customCss';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

const TSHFooterInfo = dynamic(() => import('@/app/components/hotel-detail/tsh/TSHFooterInfo'), {
  ssr: false,
});

const BeforeYouBookTSH = dynamic(() => import('@/app/components/hotel-detail/BeforeYouBookTSH'), {
  ssr: false,
});

const Atol = dynamic(() => import('@/app/components/hotel-detail/Atol'), {
  ssr: false,
});

type HotelPageBeforeYouBookSectionProps = {
  tshUrl?: string;
  atol: boolean;
} & HTMLAttributes<HTMLElement>;

export default function HotelPageBeforeYouBookSection(props: HotelPageBeforeYouBookSectionProps) {
  const { atol, tshUrl, ...rest } = props;
  const { t } = useTranslation();
  const { accommodationOnly } = useUrlSearchParams();
  const { data: staticData } = useFetchHotelStatic();
  const isTopSecret = staticData?.isTopSecret;
  const showAtol = !accommodationOnly && atol;
  if (!isTopSecret && !showAtol) return null;
  return staticData ? (
    <HotelPageContentSection
      {...rest}
      title={t('vacation.select.section.goodtoknow.title')}
      css={css`
        background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
        @media ${customCss.breakpoints.sm} {
          background-color: transparent;
        }
      `}
    >
      <div
        css={`
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        {showAtol && <Atol />}
        {isTopSecret && tshUrl && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;

              @media ${customCss.breakpoints.sm} {
                border-radius: 8px;
                background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
                padding: 16px 24px;
              }
            `}
          >
            <BeforeYouBookTSH tshUrl={tshUrl} />
          </div>
        )}
        {isTopSecret && (
          <div
            css={css`
              background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer01};
              padding-top: 16px;
              margin: 0 -16px -16px -16px;

              @media ${customCss.breakpoints.sm} {
                padding-top: 0;
                margin: 0;
              }
            `}
          >
            <TSHFooterInfo
              css={`
                padding: 12px 16px;

                @media ${customCss.breakpoints.sm} {
                  padding: 16px 24px;
                }
              `}
            />
          </div>
        )}
      </div>
    </HotelPageContentSection>
  ) : undefined;
}

// `inModal` renders the section as a tab block of the Evo hotel-info modal: smaller heading and
// the tab's title-to-content rhythm. The hotel page itself leaves it unset (MPH-839).
export function HotelPageBeforeYouBookSectionEvo({
  inModal = false,
  ...props
}: HTMLAttributes<HTMLElement> & { inModal?: boolean }) {
  const { t } = useTranslation();
  const { accommodationOnly } = useUrlSearchParams();
  const { data: staticData } = useFetchHotelStatic();
  const { atol, tshUrl } = useInitialParams();
  const SectionContainer = inModal ? EvoModalSectionContainer : EvoSectionContainer;

  const showAtol = !accommodationOnly && atol;
  // MPH-935: a Top Secret booking has its own terms — what you are and are not told about the hotel
  // before you pay, and the conditions the opaque rate rides on — and they carry regardless of ATOL,
  // which a hotel-only stay never has. The section used to return null on every hotel-only search,
  // so on Top Secret it dropped the one set of terms that had to be there.
  const showTopSecret = !!staticData?.isTopSecret;
  if (!showAtol && !showTopSecret) return null;
  return staticData ? (
    <SectionContainer {...props}>
      <EvoSectionHeading $inModal={inModal}>{t('vacation.select.section.goodtoknow.title')}</EvoSectionHeading>
      {showAtol && (
        <EvoAtolContainer $inModal={inModal}>
          <Atol />
        </EvoAtolContainer>
      )}
      {showTopSecret && (
        <EvoTopSecretContainer>
          {tshUrl && <BeforeYouBookTSH tshUrl={tshUrl} textVariant="Body" />}
          <TSHFooterInfo textVariant="Body" />
        </EvoTopSecretContainer>
      )}
    </SectionContainer>
  ) : undefined;
}

// The Evo section already provides the card and its padding, so the Top Secret blocks only need to
// stack — unlike the legacy layout, where each one paints its own panel. The gap and the panel's own
// padding are the page's space300 (24px), so the block keeps the rhythm of the sections around it
// rather than the tighter legacy one (MPH-935).
const EvoTopSecretContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space300};

  /* TSHFooterInfo paints the lavender panel edge to edge; inset its columns so the copy lines up
     with the terms above it instead of sitting against the panel's edge. */
  > div:last-child {
    padding: ${({ theme }) => theme.kallisto.space.space300};
    border-radius: ${({ theme }) => theme.kallisto.space.space100};
  }
`;

/* The two protection cards go side by side from sm on the hotel page, but only from lg inside the
   modal (MPH-839): the modal column is narrower, so the pair doesn't fit at tablet and tablet keeps
   the mobile stack. */
const EvoAtolContainer = styled.div<{ $inModal?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${({ $inModal }) =>
    !$inModal &&
    css`
      @media ${customCss.breakpoints.sm} {
        flex-direction: row;
        gap: 40px;
      }
    `}

  @media ${customCss.breakpoints.lg} {
    flex-direction: row;
    gap: 40px;
  }
`;
